import { type InputHTMLAttributes, type SelectHTMLAttributes, forwardRef } from 'react'
import { cn } from '../../lib/utils'
import { Input as ShadcnInput } from './shadcn/input'

interface FieldWrapperProps {
  label?: string
  error?: string
  hint?: string
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & FieldWrapperProps
>(function Input({ label, error, hint, id, className = '', ...props }, ref) {
  return (
    <label className="block" htmlFor={id}>
      {label && <span className="mb-1 block text-ui-label text-foreground">{label}</span>}
      <ShadcnInput ref={ref} id={id} className={cn('h-9', className)} {...props} />
      {hint && !error && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-danger-600">{error}</span>}
    </label>
  )
})

// Kept as a plain native <select> restyled to match shadcn/ui's new field
// tokens (border-input, focus-visible ring, etc.), rather than swapping in
// shadcn's Radix-based Select -- that component's multi-part API
// (SelectTrigger/SelectContent/SelectItem) doesn't accept bare <option>
// children, and every call site across this app (Rules, MessagingTestSend,
// Channels, BillingOverview, ...) uses <Select><option/></Select>. A
// native select styled with the same tokens gets the same look with zero
// call-site churn; only a menu that needs custom item rendering would
// justify pulling in the full Radix version.
const selectFieldClasses =
  'flex h-9 w-full min-w-0 items-center rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm text-foreground ' +
  'transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 ' +
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50'

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & FieldWrapperProps
>(function Select({ label, error, hint, id, className = '', children, ...props }, ref) {
  return (
    <label className="block" htmlFor={id}>
      {label && <span className="mb-1 block text-ui-label text-foreground">{label}</span>}
      <select ref={ref} id={id} className={cn(selectFieldClasses, className)} {...props}>
        {children}
      </select>
      {hint && !error && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-danger-600">{error}</span>}
    </label>
  )
})
